/**
 * Pure state derivation for the maintenance banner.
 *
 * Inputs are the announced window (from the backend or the localStorage
 * snapshot), the server-corrected clock, whether the backend answered in this
 * session, and the per-announcement dismiss markers. No I/O, no React: the hook
 * feeds it, and the tests pin every row of the spec's state table.
 *
 * `overdue` exists because a deployment may run past its announced end. While
 * the backend is still not answering, the banner keeps saying "maintenance is
 * still in progress" instead of the wrong "completed".
 */
export interface MaintenanceWindow {
  /** ISO 8601 in UTC, as delivered by the backend. */
  start: string;
  end: string;
  message: string | null;
}

export interface MaintenanceDismissed {
  /** `window.start` of the announcement that was dismissed in that state. */
  planned?: string;
  done?: string;
}

export type MaintenanceContext = 'login' | 'portal';

export type MaintenanceBannerState =
  | { kind: 'hidden' }
  | { kind: 'planned'; window: MaintenanceWindow }
  | { kind: 'running'; window: MaintenanceWindow; overdue: boolean }
  | { kind: 'done'; window: MaintenanceWindow }
  | { kind: 'unreachable' };

/** The planned banner appears this long before the window starts. */
export const PLANNED_LEAD_MS = 24 * 60 * 60 * 1000;
/** "Completed" (and "still in progress") stay at most this long after the window ends. */
export const DONE_VISIBLE_MS = 2 * 60 * 60 * 1000;

export interface DeriveMaintenanceStateInput {
  window: MaintenanceWindow | null;
  /** Milliseconds since epoch, already corrected by the server clock offset. */
  now: number;
  /** `null` until the backend answered (or failed) once in this session. */
  reachable: boolean | null;
  dismissed: MaintenanceDismissed;
  context: MaintenanceContext;
}

export const isValidMaintenanceWindow = (window: MaintenanceWindow | null): window is MaintenanceWindow => {
  if (!window) return false;
  const start = Date.parse(window.start);
  const end = Date.parse(window.end);
  return Number.isFinite(start) && Number.isFinite(end) && end > start;
};

const withoutWindow = (reachable: boolean | null, context: MaintenanceContext): MaintenanceBannerState =>
  reachable === false && context === 'login' ? { kind: 'unreachable' } : { kind: 'hidden' };

export const deriveMaintenanceState = ({
  window,
  now,
  reachable,
  dismissed,
  context,
}: DeriveMaintenanceStateInput): MaintenanceBannerState => {
  if (!isValidMaintenanceWindow(window)) return withoutWindow(reachable, context);

  const start = Date.parse(window.start);
  const end = Date.parse(window.end);

  if (now < start - PLANNED_LEAD_MS || now >= end + DONE_VISIBLE_MS) return withoutWindow(reachable, context);

  if (now < start) {
    return dismissed.planned === window.start ? { kind: 'hidden' } : { kind: 'planned', window };
  }

  if (now < end) return { kind: 'running', window, overdue: false };

  // end <= now < end + DONE_VISIBLE_MS: only a confirmed answer may decide
  // between "completed" and "still in progress".
  if (reachable === false) return { kind: 'running', window, overdue: true };
  if (reachable === null) return { kind: 'hidden' };
  return dismissed.done === window.start ? { kind: 'hidden' } : { kind: 'done', window };
};
